import { format, formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { supabase } from '@/integrations/supabase/client';

/**
 * Get user's timezone from their profile
 */
export const getUserTimezone = async (userId: string): Promise<string> => {
  try {
    // First try to get from clients table
    const { data: clientData, error: clientError } = await supabase
      .from('clients')
      .select('timezone')
      .eq('id', userId)
      .maybeSingle();

    if (clientData?.timezone) {
      return clientData.timezone;
    }

    // If not found in clients, try practitioners table
    const { data: practitionerData, error: practitionerError } = await supabase
      .from('practitioners')
      .select('timezone')
      .eq('id', userId)
      .maybeSingle();

    if (practitionerData?.timezone) {
      return practitionerData.timezone;
    }

    // If errors occurred, log them
    if (clientError && practitionerError) {
      console.error('Error fetching user timezone from both tables:', { clientError, practitionerError });
    }

    // Fallback to browser-detected timezone instead of hardcoded default
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (error) {
    console.error('Error in getUserTimezone:', error);
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  }
};

/**
 * Convert a date to user's timezone for display
 */
export const formatDateInUserTimezone = (
  date: Date | string,
  userTimezone: string,
  formatString: string = 'yyyy-MM-dd'
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  try {
    return formatInTimeZone(dateObj, userTimezone, formatString);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return format(dateObj, formatString);
  }
};

/**
 * Convert a local date to UTC for database storage
 */
export const convertLocalDateToUTC = (localDate: Date, userTimezone: string): Date => {
  try {
    // Create a date in the user's timezone at midnight
    const zonedDate = new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 0, 0, 0, 0);
    return fromZonedTime(zonedDate, userTimezone);
  } catch (error) {
    console.error('Error converting local date to UTC:', error);
    return localDate;
  }
};

/**
 * Convert UTC date to user's local timezone
 */
export const convertUTCToLocalDate = (utcDate: Date | string, userTimezone: string): Date => {
  const dateObj = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  
  try {
    return toZonedTime(dateObj, userTimezone);
  } catch (error) {
    console.error('Error converting UTC to local date:', error);
    return dateObj;
  }
};

/**
 * Get a date string in YYYY-MM-DD format for local storage (no timezone conversion)
 */
export const getLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Format date for display based on user's language preference
 */
export const formatDateForUserDisplay = (
  date: Date | string,
  userTimezone: string,
  language: string = 'en'
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  try {
    if (language === 'zh') {
      const formatted = formatInTimeZone(dateObj, userTimezone, 'yyyy年MM月dd日');
      return formatted;
    } else {
      const formatted = formatInTimeZone(dateObj, userTimezone, 'MM/dd/yyyy');
      return formatted;
    }
  } catch (error) {
    console.error('Error formatting date for display:', error);
    // Fallback to basic formatting
    if (language === 'zh') {
      return `${dateObj.getFullYear()}年${(dateObj.getMonth() + 1)}月${dateObj.getDate()}日`;
    } else {
      return `${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getDate().toString().padStart(2, '0')}/${dateObj.getFullYear()}`;
    }
  }
};

/**
 * Check if a date string is in the user's timezone today
 */
export const isToday = (dateString: string, userTimezone: string): boolean => {
  try {
    const date = new Date(dateString);
    const today = new Date();
    
    const dateInUserTz = formatInTimeZone(date, userTimezone, 'yyyy-MM-dd');
    const todayInUserTz = formatInTimeZone(today, userTimezone, 'yyyy-MM-dd');
    
    return dateInUserTz === todayInUserTz;
  } catch (error) {
    console.error('Error checking if date is today:', error);
    return false;
  }
};